import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout } from 'antd';
import PortalHeader from './PortalHeader';
import PortalFooter from './PortalFooter';
import RouteFallback from '../RouteFallback';
import Seo from '../Seo';
import { API_BASE_URL } from '../../config/api';

// Loaded on demand: the bubble only appears once the official-KB fetch below
// resolves, and its markdown renderer is one of the heaviest things on the
// marketing pages — no reason for it to sit in the entry bundle.
const ChatWidget = lazy(() => import('../ChatWidget'));

const { Content } = Layout;

type OfficialKb = { id: string; name: string; slug: string | null; api_key: string };

// Runtime-fetched: the CI bootstrap (e2e/scripts/bootstrap-self-help-kb.mjs)
// creates the official KB and sets its public api_key dynamically per env, so
// the frontend can't bake the key at build time. Hitting
// /api/discover/official-kbs on mount keeps the widget in sync with whatever
// the bootstrap last provisioned — and gracefully shows nothing in
// environments where the bootstrap hasn't run yet (fresh sandbox, localhost).
const PortalLayout: React.FC = () => {
  const [officialKb, setOfficialKb] = useState<OfficialKb | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/discover/official-kbs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list: OfficialKb[]) => {
        if (cancelled) return;
        if (list && list.length > 0) setOfficialKb(list[0]);
      })
      .catch(() => {
        // Network / CORS / 5xx — silently skip the widget. Portal pages
        // still render normally; the bubble is non-essential.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Seo />
      <PortalHeader />
      <Content>
        {/* Page chunks suspend here rather than at the app root, so the header
            and footer stay painted while the route loads. */}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </Content>
      <PortalFooter />
      {officialKb && (
        <Suspense fallback={null}>
          <ChatWidget apiKey={officialKb.api_key} knowledgeBaseId={officialKb.id} />
        </Suspense>
      )}
    </Layout>
  );
};

export default PortalLayout;
